import { useEffect } from "react"
import { message } from "antd"

import CallbackToast from "./callback-toast"
import { BulkDialer4L, Dialer4L } from "./dialer"
import { declineIncomingCall, sendCallToVoicemail } from "~/api/voice"
import { useVoiceDevice } from "~/hooks/use-voice-device"
import useGlobalStore from "~/store/global"
import useVoiceSessionStore, {
  useCallSessionActive
} from "~/store/voice-session"
import { holdCallLock } from "~/utils/call-lock"

function VoiceDialerInner() {
  const tokenInfo = useGlobalStore((s) => s.tokenInfo)
  const bulkPeople = useVoiceSessionStore((s) => s.bulkPeople)
  const single = useVoiceSessionStore((s) => s.single)
  const closeBulk = useVoiceSessionStore((s) => s.closeBulk)
  const closeSingle = useVoiceSessionStore((s) => s.closeSingle)
  const inbound = useVoiceSessionStore((s) => s.inbound)
  const startInbound = useVoiceSessionStore((s) => s.startInbound)
  const closeInbound = useVoiceSessionStore((s) => s.closeInbound)

  // Held here rather than inside the dialers so a callback rings whether or not the rep has the
  // dialer open. The device is shared, so this does not create a second registration.
  const { incoming, answerIncoming, rejectIncoming } = useVoiceDevice()

  // Declining hangs up the rep's leg and leaves any other leg of the dial alone. Voicemail ends
  // the dial for everyone and takes a message, which only the service can do, so the rep's own
  // leg is released either way and the outcome differs upstream.
  // Picking up a callback should land the rep in the same panel a placed call gives them —
  // who it is, what to say, profile, mute, hold — rather than an open mic and no context.
  const acceptIncoming = () => {
    const caller = incoming
    const call = answerIncoming()
    if (!caller || !call) return
    startInbound({
      person: {
        id: caller.peopleId ?? caller.from,
        name: caller.name ?? caller.from,
        title: caller.title ?? undefined,
        phone: caller.from
      },
      call
    })
  }

  // Declining is about the call, not about this device. Dropping only the browser leg leaves the
  // rep's own phone ringing a call they have just refused.
  const endIncoming = (
    action: "decline" | "voicemail",
    send: (
      token: NonNullable<typeof tokenInfo>,
      sid: string
    ) => Promise<unknown>
  ) => {
    const parentSid = incoming?.parentSid
    rejectIncoming()
    if (!tokenInfo) return
    if (!parentSid) {
      message.error(
        action === "voicemail"
          ? "Could not send that call to voicemail — it is still ringing."
          : "Could not decline that call — it is still ringing."
      )
      return
    }
    send(tokenInfo, parentSid).catch(() => {
      message.error(
        action === "voicemail"
          ? "Could not send that call to voicemail — it may still be ringing."
          : "Could not decline that call — it may still be ringing."
      )
    })
  }

  const declineIncoming = () => endIncoming("decline", declineIncomingCall)

  const voicemailIncoming = () => endIncoming("voicemail", sendCallToVoicemail)

  return (
    <>
      {incoming && (
        <CallbackToast
          caller={{
            number: incoming.from,
            name: incoming.name,
            title: incoming.title,
            lastCalledAt: incoming.lastCalledAt
          }}
          inSession={Boolean(bulkPeople)}
          midCall={Boolean(incoming.midCall)}
          onAnswer={acceptIncoming}
          onDecline={declineIncoming}
          onVoicemail={voicemailIncoming}
          onMissed={rejectIncoming}
        />
      )}
      {single && (
        <Dialer4L person={single} onClose={closeSingle} onLogCall={() => {}} />
      )}
      {inbound && (
        <Dialer4L
          person={inbound.person}
          inbound={inbound}
          onClose={closeInbound}
          onLogCall={() => {}}
        />
      )}
      {bulkPeople && (
        <BulkDialer4L
          people={bulkPeople}
          onClose={closeBulk}
          onLogCall={() => {}}
          onEnrich={() => {}}
        />
      )}
    </>
  )
}

export default function VoiceDialerHost() {
  // Key Actions' dialer CTA opens this same panel, and that surface is already
  // behind unified_os_key_actions_enabled — so it gates the host too rather
  // than requiring people_os on top. Without this the CTA sets the session and
  // nothing renders.
  const enabled = useGlobalStore(
    (s) =>
      s.user?.feature_flags?.people_os ||
      s.user?.feature_flags?.unified_os_key_actions_enabled
  )
  const callingEnabled = useGlobalStore(
    (s) => s.user?.feature_flags?.calling_enabled
  )
  const sessionActive = useCallSessionActive()
  // Mounted app-wide, so this is the one place that outlives every dialer panel and
  // can be trusted to release the lock when the last one closes.
  useEffect(() => holdCallLock(sessionActive), [sessionActive])

  if (!enabled) return null

  return (
    <div className="voice-dialer-root">
      {callingEnabled && <VoiceDialerInner />}
    </div>
  )
}
